import React, { useState, useEffect, useCallback } from "react";
import { authFetch } from "../pages/Auth";
import { COLORS } from "../chartTheme";

// ═══════════════════════════════════════════════════════════
// LINKEDIN AGENT PANEL
// ═══════════════════════════════════════════════════════════
// Phase 1: Generate → review → edit → mark approved/posted.
// No actual LinkedIn API call yet — "Mark posted" is manual for now.

const cardStyle = {
  background: COLORS.bg,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: 20,
  marginBottom: 16,
};

const STATUS_COLORS = {
  pending: { bg: "rgba(251,191,36,0.12)", fg: "#fbbf24" },
  approved: { bg: "rgba(52,211,153,0.15)", fg: "#34d399" },
  rejected: { bg: "rgba(255,77,109,0.15)", fg: "#ff4d6d" },
  posted: { bg: "rgba(0,229,255,0.15)", fg: "#00e5ff" },
};

function Spinner({ label = "Loading..." }) {
  return (
    <div style={{ textAlign: "center", padding: 30, color: COLORS.muted, fontSize: 12 }}>
      {label}
    </div>
  );
}

function ErrorBanner({ msg, onRetry }) {
  if (!msg) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "rgba(255,77,109,0.08)",
        border: "1px solid rgba(255,77,109,0.25)",
        borderRadius: 8,
        padding: "10px 14px",
        marginBottom: 12,
        fontSize: 12,
        color: "#ff4d6d",
      }}
    >
      <span>{msg}</span>
      {onRetry && (
        <button onClick={onRetry} style={s.retryBtn}>
          Retry
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Draft card (single post variant)
// ─────────────────────────────────────────────────────────────

function DraftCard({ draft, onUpdate, onDelete }) {
  const [content, setContent] = useState(draft.content || "");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setContent(draft.content || "");
    setDirty(false);
  }, [draft.content]);

  const handleContentChange = useCallback((e) => {
    setContent(e.target.value);
    setDirty(true);
  }, []);

  const patch = useCallback(
    async (body) => {
      setSaving(true);
      try {
        const res = await authFetch(`/api/linkedin/drafts/${draft.draft_id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Update failed");
        onUpdate();
      } catch (err) {
        // eslint-disable-next-line no-alert
        alert(err.message || "Update failed");
      } finally {
        setSaving(false);
      }
    },
    [draft.draft_id, onUpdate]
  );

  const handleSaveEdits = useCallback(() => {
    patch({ content });
    setDirty(false);
  }, [patch, content]);

  const handleApprove = useCallback(() => {
    const body = { status: "approved" };
    if (dirty) body.content = content;
    patch(body);
    setDirty(false);
  }, [patch, dirty, content]);

  const handleReject = useCallback(() => patch({ status: "rejected" }), [patch]);
  const handleMarkPosted = useCallback(() => patch({ status: "posted" }), [patch]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: select the textarea
    }
  }, [content]);

  const statusColor = STATUS_COLORS[draft.status] || STATUS_COLORS.pending;
  const charCount = content.length;
  const charColor = charCount > 1300 ? "#ff4d6d" : charCount < 400 ? "#fbbf24" : COLORS.muted;

  return (
    <div
      style={{
        background: "#0d1117",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: 16,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              ...s.badge,
              background: statusColor.bg,
              color: statusColor.fg,
            }}
          >
            {draft.status}
          </span>
          <span style={{ fontSize: 11, color: COLORS.muted }}>
            {draft.angle?.replace(/_/g, " ")}
          </span>
          {typeof draft.variant_index === "number" && (
            <span style={{ fontSize: 10, color: COLORS.muted }}>
              · variant {draft.variant_index + 1}
            </span>
          )}
        </div>
        <div style={{ fontSize: 10, color: charColor }}>{charCount} chars</div>
      </div>

      <textarea
        value={content}
        onChange={handleContentChange}
        rows={8}
        style={{
          width: "100%",
          background: "#131920",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 6,
          padding: 12,
          color: COLORS.text,
          fontSize: 13,
          fontFamily: "inherit",
          lineHeight: 1.5,
          resize: "vertical",
          outline: "none",
          boxSizing: "border-box",
        }}
      />

      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button
          onClick={handleCopy}
          style={{ ...s.smallBtn, background: "rgba(0,229,255,0.12)", color: "#00e5ff" }}
          disabled={saving}
        >
          {copied ? "Copied!" : "Copy"}
        </button>
        {dirty && (
          <button
            onClick={handleSaveEdits}
            style={{ ...s.smallBtn, background: "#131920", color: COLORS.text }}
            disabled={saving}
          >
            Save edits
          </button>
        )}
        {draft.status !== "approved" && (
          <button
            onClick={handleApprove}
            style={{ ...s.smallBtn, background: "rgba(52,211,153,0.15)", color: "#34d399" }}
            disabled={saving}
          >
            Approve
          </button>
        )}
        {draft.status === "approved" && (
          <button
            onClick={handleMarkPosted}
            style={{ ...s.smallBtn, background: "rgba(0,229,255,0.15)", color: "#00e5ff" }}
            disabled={saving}
          >
            Mark as posted
          </button>
        )}
        {draft.status !== "rejected" && (
          <button
            onClick={handleReject}
            style={{ ...s.smallBtn, background: "rgba(251,191,36,0.12)", color: "#fbbf24" }}
            disabled={saving}
          >
            Reject
          </button>
        )}
        <button
          onClick={() => {
            // eslint-disable-next-line no-alert
            if (window.confirm("Delete this draft permanently?")) onDelete(draft.draft_id);
          }}
          style={{ ...s.smallBtn, background: "rgba(255,77,109,0.12)", color: "#ff4d6d" }}
          disabled={saving}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────

export default function LinkedInAgentPanel() {
  const [angles, setAngles] = useState([]);
  const [form, setForm] = useState({
    angle: "clinical_insight",
    topic: "",
    context: "",
    variants: 3,
    include_stats: true,
  });
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");

  const [drafts, setDrafts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [stats, setStats] = useState(null);
  const [showStats, setShowStats] = useState(false);

  // Load angles once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await authFetch("/api/linkedin/angles");
        const data = await res.json();
        if (alive && res.ok) setAngles(data.angles || []);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const loadDrafts = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const res = await authFetch(`/api/linkedin/drafts${q}`);
      const data = await res.json();
      if (res.ok) setDrafts(data.drafts || []);
      else setLoadError(data.error || "Failed to load drafts");
    } catch {
      setLoadError("Unable to connect");
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const loadStats = useCallback(async () => {
    try {
      const res = await authFetch("/api/linkedin/stats-preview");
      const data = await res.json();
      if (res.ok) setStats(data);
    } catch {
      /* ignore */
    }
  }, []);

  const handleToggleStats = useCallback(() => {
    setShowStats((prev) => {
      const next = !prev;
      if (next && !stats) loadStats();
      return next;
    });
  }, [stats, loadStats]);

  const handleGenerate = useCallback(
    async (e) => {
      e.preventDefault();
      setGenerating(true);
      setGenError("");
      try {
        const res = await authFetch("/api/linkedin/draft", {
          method: "POST",
          body: JSON.stringify({
            angle: form.angle,
            topic: form.topic,
            context: form.context,
            variants: Number(form.variants) || 3,
            include_stats: form.include_stats,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Generation failed");
        await loadDrafts();
      } catch (err) {
        setGenError(err.message || "Generation failed");
      }
      setGenerating(false);
    },
    [form, loadDrafts]
  );

  const handleDelete = useCallback(
    async (draftId) => {
      try {
        const res = await authFetch(`/api/linkedin/drafts/${draftId}`, {
          method: "DELETE",
        });
        if (res.ok) loadDrafts();
      } catch {
        /* ignore */
      }
    },
    [loadDrafts]
  );

  const angleDef = angles.find((a) => a.id === form.angle);

  return (
    <div>
      {/* ─── Generation form ─── */}
      <div style={cardStyle}>
        <div style={s.secTitle}>Generate LinkedIn Drafts</div>
        <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 14 }}>
          Claude generates post variants using aggregate (k-anonymized) platform stats. No PHI
          leaves GCP. You review, edit, and post manually.
        </div>

        <ErrorBanner msg={genError} />

        <form onSubmit={handleGenerate}>
          <div style={{ ...s.formGrid, gridTemplateColumns: "1fr 1fr" }}>
            <div>
              <label style={s.label}>Angle</label>
              <select
                value={form.angle}
                onChange={(e) => setForm((f) => ({ ...f, angle: e.target.value }))}
                style={s.input}
              >
                {angles.length === 0 && <option value="clinical_insight">Clinical Insight</option>}
                {angles.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label} ({a.voice})
                  </option>
                ))}
              </select>
              {angleDef && (
                <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 4 }}>
                  {angleDef.description}
                </div>
              )}
            </div>
            <div>
              <label style={s.label}>Variants (1–5)</label>
              <input
                type="number"
                min={1}
                max={5}
                value={form.variants}
                onChange={(e) => setForm((f) => ({ ...f, variants: e.target.value }))}
                style={s.input}
              />
            </div>
          </div>

          <label style={s.label}>Topic / hook</label>
          <input
            type="text"
            placeholder='e.g. "why 40% of GLP-1 patients drop out by month 6"'
            value={form.topic}
            onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))}
            style={{ ...s.input, width: "100%", marginBottom: 10, boxSizing: "border-box" }}
          />

          <label style={s.label}>Extra context (optional)</label>
          <textarea
            rows={2}
            placeholder="anything else to include — a news event, a customer conversation, a stat you want front-and-center"
            value={form.context}
            onChange={(e) => setForm((f) => ({ ...f, context: e.target.value }))}
            style={{
              ...s.input,
              width: "100%",
              marginBottom: 10,
              boxSizing: "border-box",
              fontFamily: "inherit",
              resize: "vertical",
            }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 4,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                color: COLORS.text,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={form.include_stats}
                onChange={(e) =>
                  setForm((f) => ({ ...f, include_stats: e.target.checked }))
                }
              />
              Inject aggregate platform stats into the prompt
            </label>
            <button
              type="button"
              onClick={handleToggleStats}
              style={{ ...s.smallBtn, background: "#131920", color: COLORS.muted }}
            >
              {showStats ? "Hide stats" : "Preview stats"}
            </button>
          </div>

          {showStats && (
            <div
              style={{
                marginTop: 10,
                padding: 12,
                background: "#0a0e13",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 11,
                color: COLORS.muted,
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
              }}
            >
              {stats ? stats.formatted : "Loading stats..."}
            </div>
          )}

          <button
            type="submit"
            disabled={generating}
            style={{ ...s.submitBtn, marginTop: 14, width: "100%" }}
          >
            {generating ? "Generating..." : "Generate drafts"}
          </button>
        </form>
      </div>

      {/* ─── Drafts list ─── */}
      <div style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <div style={s.secTitle}>Drafts</div>
          <div style={{ display: "flex", gap: 4 }}>
            {["", "pending", "approved", "posted", "rejected"].map((sv) => (
              <button
                key={sv || "all"}
                onClick={() => setStatusFilter(sv)}
                style={{
                  ...s.filterBtn,
                  background: statusFilter === sv ? "rgba(0,229,255,0.1)" : "#131920",
                  color: statusFilter === sv ? COLORS.primary : COLORS.muted,
                }}
              >
                {sv || "all"}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <Spinner />
        ) : loadError ? (
          <ErrorBanner msg={loadError} onRetry={loadDrafts} />
        ) : !drafts || drafts.length === 0 ? (
          <div style={{ textAlign: "center", padding: 30, color: COLORS.muted, fontSize: 12 }}>
            No drafts yet. Generate some above.
          </div>
        ) : (
          drafts.map((d) => (
            <DraftCard key={d.draft_id} draft={d} onUpdate={loadDrafts} onDelete={handleDelete} />
          ))
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────

const s = {
  secTitle: { fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 6 },
  label: {
    display: "block",
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: COLORS.muted,
    marginBottom: 4,
    marginTop: 8,
  },
  input: {
    background: "#131920",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    padding: "8px 12px",
    color: COLORS.text,
    fontSize: 12,
    fontFamily: "inherit",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 8 },
  submitBtn: {
    background: "linear-gradient(135deg, #00e5ff, #0099cc)",
    border: "none",
    borderRadius: 6,
    color: "#000",
    padding: "10px 18px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  smallBtn: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    padding: "6px 12px",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  retryBtn: {
    background: "rgba(255,77,109,0.15)",
    border: "1px solid rgba(255,77,109,0.3)",
    borderRadius: 6,
    color: "#ff4d6d",
    padding: "4px 10px",
    fontSize: 10,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  filterBtn: {
    borderRadius: 6,
    padding: "5px 12px",
    fontSize: 10,
    cursor: "pointer",
    fontWeight: 600,
    fontFamily: "inherit",
    border: `1px solid ${COLORS.border}`,
    textTransform: "capitalize",
  },
  badge: {
    padding: "2px 10px",
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
};
